<script setup lang="ts">
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  type Placement,
  type Strategy,
} from "@floating-ui/vue";
import { ref, useTemplateRef, computed, onUnmounted } from "vue";

interface FloatingTooltipProps {
  text?: string;
  placement?: Placement;
  enabled?: boolean;
  strategy?: Strategy;
  persist?: boolean;
}
const {
  placement = "bottom",
  enabled = true,
  strategy = "fixed",
  persist = false,
} = defineProps<FloatingTooltipProps>();

const isHover = ref(false);
const isReferenceHovered = ref(false);
const isFloatingHovered = ref(false);
const gracePeriodActive = ref(false);
let gracePeriodTimeout: ReturnType<typeof setTimeout> | null = null;

const reference = useTemplateRef<HTMLElement>("reference");
const floating = useTemplateRef<HTMLDivElement>("floating");
const { floatingStyles } = useFloating(reference, floating, {
  placement,
  strategy,
  middleware: [
    offset(10),
    shift(),
    flip({ crossAxis: false, fallbackAxisSideDirection: placement === "bottom" ? "end" : "start" }),
    size({
      apply({ availableWidth, availableHeight, elements }) {
        const { width, height } = elements.floating.getBoundingClientRect();
        const styles: { [key: string]: string } = {};
        if (width > availableWidth) {
          styles["maxWidth"] = `${Math.max(0, availableWidth)}px`;
        }
        if (height > availableHeight) {
          styles["maxHeight"] = `${Math.max(0, availableHeight)}px`;
        }
        Object.assign(elements.floating.style, styles);
      },
    }),
  ],
  whileElementsMounted: autoUpdate,
});

const isVisiblePersist = computed(
  () => enabled && (isReferenceHovered.value || isFloatingHovered.value || gracePeriodActive.value)
);

const isVisible = computed(() => (persist ? isVisiblePersist.value : enabled && isHover.value));

function onReferenceLeave() {
  isReferenceHovered.value = false;
  gracePeriodActive.value = true;
  if (gracePeriodTimeout) clearTimeout(gracePeriodTimeout);
  gracePeriodTimeout = setTimeout(() => {
    gracePeriodActive.value = false;
    gracePeriodTimeout = null;
  }, 150);
}

function onReferenceEnter() {
  isReferenceHovered.value = true;
  if (gracePeriodTimeout) {
    clearTimeout(gracePeriodTimeout);
    gracePeriodTimeout = null;
  }
  gracePeriodActive.value = false;
}

onUnmounted(() => {
  if (gracePeriodTimeout) clearTimeout(gracePeriodTimeout);
});
</script>

<template>
  <span
    class="floating-tooltip"
    ref="reference"
    @mouseenter="persist ? onReferenceEnter() : (isHover = true)"
    @mouseleave="persist ? onReferenceLeave() : (isHover = false)"
  >
    <slot></slot>
    <Teleport to="body">
      <Transition name="fade">
        <span
          v-if="isVisible"
          ref="floating"
          class="floating-tooltip-content shadow-md"
          :class="placement"
          :style="floatingStyles"
          @mouseenter="persist && (isFloatingHovered = true)"
          @mouseleave="persist && (isFloatingHovered = false)"
        >
          <template v-if="$slots.tooltipContent">
            <slot name="tooltipContent"></slot>
          </template>
          <template v-else>
            {{ text }}
          </template>
        </span>
      </Transition>
    </Teleport>
  </span>
</template>

<style scoped>
.floating-tooltip-content {
  position: fixed;
  z-index: 9000;
  width: max-content;
  max-width: 80dvw;
  padding: var(--numbers-6);
  border-radius: var(--numbers-4);
  background-color: var(--background-inverse);
  color: var(--content-primary-inverse);
}
</style>
